/**
 * Cookie Preference Center Modal
 * Allows users to customize their cookie preferences
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Divider,
  Link,
  Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useCookieConsent, CookiePreferences } from '../../hooks/useCookieConsent';
import { havenColors } from '../../theme/theme';

interface CookiePreferenceCenterProps {
  open: boolean;
  onClose: () => void;
}

const CookiePreferenceCenter: React.FC<CookiePreferenceCenterProps> = ({
  open,
  onClose,
}) => {
  const { preferences, saveCustomPreferences, acceptAll } = useCookieConsent();
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(preferences);
  const [wasOpen, setWasOpen] = useState(open);

  // Reset local edits to the saved preferences each time the modal opens.
  // Adjusting state during render in response to a prop change is the
  // React-recommended pattern (https://react.dev/learn/...#adjusting-state).
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setLocalPreferences(preferences);
    }
  }

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    saveCustomPreferences({
      analytics: localPreferences.analytics,
      marketing: localPreferences.marketing,
    });
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
          },
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `2px solid ${havenColors.lightGray}`,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: havenColors.darkGray }}>
          Cookie Preferences
        </Typography>
        <Typography variant="body2" sx={{ color: havenColors.secondaryGrey, mt: 1 }}>
          Manage your cookie preferences. You can enable or disable different types of cookies below.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Necessary Cookies */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: havenColors.darkGray }}>
                Necessary Cookies
              </Typography>
              <Typography variant="body2" sx={{ color: havenColors.secondaryGrey, mt: 0.5 }}>
                These cookies are essential for the website to function properly. They cannot be disabled.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={true}
                  disabled
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-disabled': {
                      color: havenColors.primaryTeal,
                    },
                    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                      backgroundColor: havenColors.primaryTeal,
                      opacity: 0.5,
                    },
                  }}
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Analytics Cookies */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: havenColors.darkGray }}>
                Analytics Cookies
              </Typography>
              <Typography variant="body2" sx={{ color: havenColors.secondaryGrey, mt: 0.5 }}>
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localPreferences.analytics}
                  onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: havenColors.primaryTeal,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: havenColors.primaryTeal,
                    },
                  }}
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Marketing Cookies */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: havenColors.darkGray }}>
                Marketing Cookies
              </Typography>
              <Typography variant="body2" sx={{ color: havenColors.secondaryGrey, mt: 0.5 }}>
                These cookies are used to deliver personalized advertisements and track campaign performance.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localPreferences.marketing}
                  onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: havenColors.primaryTeal,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: havenColors.primaryTeal,
                    },
                  }}
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mt: 3, backgroundColor: havenColors.lightYellow }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            For more information about how we use cookies, please read our{' '}
            <Link
              component={RouterLink}
              to="/cookies"
              onClick={onClose}
              sx={{
                color: havenColors.primaryTeal,
                fontWeight: 500,
                textDecoration: 'underline',
                '&:hover': {
                  color: havenColors.darkTeal,
                },
              }}
            >
              Cookie Policy
            </Link>
            .
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${havenColors.lightGray}`,
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: havenColors.secondaryGrey,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAcceptAll}
          variant="outlined"
          sx={{
            borderColor: havenColors.secondaryGrey,
            color: havenColors.darkGray,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: havenColors.darkGray,
              backgroundColor: havenColors.lightGray,
            },
          }}
        >
          Accept All
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.lightTeal} 100%)`,
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 14px 0 rgba(0, 196, 193, 0.39)',
            '&:hover': {
              background: `linear-gradient(135deg, ${havenColors.darkTeal} 0%, ${havenColors.primaryTeal} 100%)`,
              boxShadow: '0 6px 20px 0 rgba(0, 196, 193, 0.5)',
            },
          }}
        >
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookiePreferenceCenter;

